import type { ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import type { AppLocale } from './lib/locale';
import { Home } from './pages/Home';
import { Simulate } from './pages/Simulate';
import { Faq } from './pages/Faq';
import { About } from './pages/About';
import { Guide } from './pages/Guide';
import { Story } from './pages/Story';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Contact } from './pages/Contact';
import { ArticleIndex } from './pages/articles/ArticleIndex';
import { HowPowerballWorks } from './pages/articles/HowPowerballWorks';
import { UnderstandingLotteryOdds } from './pages/articles/UnderstandingLotteryOdds';
import { HistoryOfAmericanLotteries } from './pages/articles/HistoryOfAmericanLotteries';
import { HowRngWorks } from './pages/articles/HowRngWorks';
import { ResponsiblePlay } from './pages/articles/ResponsiblePlay';
import { KoreanLotto645 } from './pages/articles/KoreanLotto645';

function LegacyRedirect() {
  const loc = useLocation();
  let path = loc.pathname.replace(/\/index\.html$/i, '/').replace(/\.html$/i, '/');
  if (path === '') path = '/';
  if (path !== loc.pathname) {
    const q = loc.search || '';
    const h = loc.hash || '';
    return <Navigate to={`${path}${q}${h}`} replace />;
  }
  return <Navigate to="/" replace />;
}

type Page = [path: string, Component: ComponentType];

const PAGES: Page[] = [
  ['/faq', Faq],
  ['/story', Story],
  ['/about', About],
  ['/guide', Guide],
  ['/privacy', Privacy],
  ['/terms', Terms],
  ['/contact', Contact],
  ['/simulate', Simulate],
];

/** Articles are English-only. */
const ARTICLES: Page[] = [
  ['/articles', ArticleIndex],
  ['/articles/how-powerball-works', HowPowerballWorks],
  ['/articles/understanding-lottery-odds', UnderstandingLotteryOdds],
  ['/articles/history-of-american-lotteries', HistoryOfAmericanLotteries],
  ['/articles/how-rng-works', HowRngWorks],
  ['/articles/responsible-lottery-play', ResponsiblePlay],
  ['/articles/korean-lotto-645', KoreanLotto645],
];

const LOCALES: [locale: AppLocale, prefix: string][] = [
  ['en', ''],
  ['ko', '/ko'],
  ['es', '/es'],
  ['zh', '/zh'],
];

/** Legacy static HTML pages (en + ko only) → SPA routes. */
const LEGACY_HTML: [from: string, to: string][] = [
  ['/faq.html', '/faq/'],
  ['/about.html', '/about/'],
  ['/story-qa.html', '/story/'],
  ['/privacy.html', '/privacy/'],
  ['/terms.html', '/terms/'],
  ['/simulate.html', '/simulate/'],
];

function localeRoutes(locale: AppLocale, prefix: string) {
  const home = prefix || '/';
  return (
    <Route key={locale} element={<AppLayout locale={locale} />}>
      <Route path={home} element={<Home />} />
      <Route path={`${prefix}/auto-generate`} element={<Navigate to={`${home}#auto-search`} replace />} />
      <Route path={`${prefix}/story-qa`} element={<Navigate to={`${prefix}/story`} replace />} />
      {PAGES.map(([path, Page]) => (
        <Route key={path} path={prefix + path} element={<Page />} />
      ))}
      {locale === 'en' &&
        ARTICLES.map(([path, Page]) => <Route key={path} path={path} element={<Page />} />)}
    </Route>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/index.html" element={<LegacyRedirect />} />
      {LOCALES.filter(([locale]) => locale !== 'en').map(([locale, prefix]) => (
        <Route key={locale} path={`${prefix}/index.html`} element={<Navigate to={`${prefix}/`} replace />} />
      ))}
      {['', '/ko'].flatMap((prefix) =>
        LEGACY_HTML.map(([from, to]) => (
          <Route key={prefix + from} path={prefix + from} element={<Navigate to={prefix + to} replace />} />
        )),
      )}

      {LOCALES.map(([locale, prefix]) => localeRoutes(locale, prefix))}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
